import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import cloud from 'd3-cloud';

// ── Stop words ────────────────────────────────────────────────────────────────

const STOP_WORDS = new Set([
  // Pronomen
  'jag', 'du', 'han', 'hon', 'vi', 'ni', 'de', 'dem', 'den', 'det', 'sig',
  'mig', 'dig', 'oss',
  // Possessiva
  'min', 'din', 'sin', 'hans', 'hennes', 'vår', 'er', 'deras',
  'mitt', 'ditt', 'sitt', 'vårt', 'ert',
  // Hjälpverb / kopula
  'är', 'var', 'har', 'hade', 'ska', 'kan', 'vill', 'bli', 'vara',
  'skulle', 'kunde', 'ville', 'borde', 'måste', 'får', 'fick', 'kommer',
  // Prepositioner
  'och', 'att', 'på', 'av', 'för', 'med', 'till', 'som', 'om', 'vid',
  'mot', 'från', 'över', 'under', 'efter', 'innan', 'sedan', 'via', 'inom',
  'utan', 'genom', 'hos', 'bland', 'utom', 'trots',
  // Konjunktioner / partiklar
  'inte', 'men', 'så', 'eller', 'samt', 'varken', 'antingen',
  // Determinativer
  'en', 'ett', 'denna', 'detta', 'dessa', 'varje', 'hela', 'båda', 'alla',
  // Adverb (fyllnadsord)
  'nu', 'här', 'när', 'då', 'dit', 'upp', 'ut', 'in', 'ner', 'bara',
  'lite', 'mer', 'mest', 'mycket', 'ganska', 'väldigt', 'rätt', 'nog',
  'ju', 'väl', 'ja', 'nej', 'redan', 'också', 'igen', 'alltid', 'aldrig',
  'ibland', 'ofta', 'just', 'nästan', 'annars', 'ändå', 'ännu', 'dock',
  'däremot', 'istället',
  // Frågeord
  'vad', 'hur', 'vem', 'varför', 'vilket', 'vilken', 'vilka',
  // Kvantifikatorer
  'något', 'några', 'allt', 'många', 'man', 'ingen', 'inget', 'inga',
  'viss', 'vissa',
  // Engelska stoppord
  'the', 'and', 'is', 'in', 'it', 'of', 'to', 'that', 'was', 'for',
  'are', 'with', 'he', 'she', 'we', 'they', 'at', 'be', 'or', 'an',
  'my', 'you', 'your', 'our', 'not', 'but', 'have', 'had',
]);

// ── Stemmer ───────────────────────────────────────────────────────────────────

const MIN_STEM = 4;

const SUFFIXES = [
  'arnas', 'ernas', 'ornas',
  'arens',
  'arna', 'erna', 'orna',
  'ares',
  'are',
  'ens',
  'ar', 'er', 'or',
  'en', 'an',
  'as', 'es',
];

function stem(word) {
  for (const suffix of SUFFIXES) {
    if (word.endsWith(suffix) && word.length - suffix.length >= MIN_STEM) {
      return word.slice(0, word.length - suffix.length);
    }
  }
  return word;
}

// Kept un-lowercased until after the acronym check, so a fully upper-case
// token like "AI" or "EU" can be recognised as an acronym and exempted from
// the 3-letter minimum below (which would otherwise treat it as noise).
function tokenize(text) {
  return (text || '')
    .replace(/[^a-zA-ZåäöÅÄÖéÉàÀüÜ\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(raw => ({
      word: raw.toLowerCase(),
      raw,
      isAcronym: raw.length >= 2 && raw === raw.toUpperCase() && raw !== raw.toLowerCase(),
    }));
}

// ── Phrase (n-gram) detection ───────────────────────────────────────────────
// Groups repeated 2-3 word sequences across DIFFERENT answers into a single
// cloud entry (e.g. "Jag tycker om att äta glass" and "Äta glass" both
// contain "äta glass", so it's shown once, sized by how many distinct
// answers used it - rather than as two separate, smaller "äta"/"glass"
// entries). Phrases are matched on the exact word sequence (no stemming),
// and unlike single-word entries, stop words are kept as phrase components
// since they're often part of the meaningful phrase (e.g. "roligt att fiska").

const MAX_PHRASE_WORDS = 3;
const MIN_PHRASE_WORDS = 2;

function hasRealContentWord(words) {
  // Reject phrases that are pure filler (e.g. "att det är") by requiring at
  // least one word that isn't a stop word and has some substance.
  return words.some(w => w.length >= 3 && !STOP_WORDS.has(w));
}

function findPhraseCandidates(tokenizedAnswers, minAnswerCount) {
  const candidates = [];

  for (let n = MAX_PHRASE_WORDS; n >= MIN_PHRASE_WORDS; n--) {
    const answerSetsByPhrase = new Map();

    tokenizedAnswers.forEach((tokens, answerIdx) => {
      for (let i = 0; i + n <= tokens.length; i++) {
        const words = tokens.slice(i, i + n).map(t => t.word);
        if (!hasRealContentWord(words)) continue;

        const key = words.join(' ');
        if (!answerSetsByPhrase.has(key)) {
          answerSetsByPhrase.set(key, new Set());
        }
        answerSetsByPhrase.get(key).add(answerIdx);
      }
    });

    for (const [phrase, answerSet] of answerSetsByPhrase) {
      if (answerSet.size >= minAnswerCount) {
        candidates.push({ phrase, words: phrase.split(' '), n, count: answerSet.size });
      }
    }
  }

  // Longer phrases first, then most common - so a confirmed 3-word phrase
  // is preferred over a 2-word phrase it overlaps with.
  candidates.sort((a, b) => b.n - a.n || b.count - a.count);
  return candidates;
}

// ── Frequency builder ─────────────────────────────────────────────────────────
// Merges confirmed phrases with single-word (stemmed, stop-word-filtered)
// entries for whatever text isn't covered by a phrase match.

function buildEntries(answerList, phraseMinAnswerCount) {
  const tokenizedAnswers = answerList.map(a => tokenize(a.answerText));

  // Eligible candidates (2-3 word sequences that recur across enough
  // distinct answers). This is only used to know what to LOOK for below -
  // whether a candidate actually becomes a cloud entry depends on whether
  // it's actually selected during the scan, so a 2-word phrase that's
  // always part of a longer, also-eligible 3-word phrase (e.g. "roligt
  // att" inside "roligt att fiska") never becomes its own entry.
  const phraseCandidates = phraseMinAnswerCount > 0
    ? findPhraseCandidates(tokenizedAnswers, phraseMinAnswerCount)
    : [];

  // Longest-match-first phrase lookup, keyed by starting word for speed.
  const phrasesByFirstWord = new Map();
  for (const candidate of phraseCandidates) {
    const firstWord = candidate.words[0];
    if (!phrasesByFirstWord.has(firstWord)) phrasesByFirstWord.set(firstWord, []);
    phrasesByFirstWord.get(firstWord).push(candidate);
  }

  const entries = {}; // key -> { display, count, isPhrase }

  for (const tokens of tokenizedAnswers) {
    const phrasesMatchedInThisAnswer = new Set();

    for (let i = 0; i < tokens.length; i++) {
      const { word, raw, isAcronym } = tokens[i];
      const possiblePhrases = phrasesByFirstWord.get(word);
      const match = possiblePhrases && possiblePhrases.find(c =>
        c.words.every((w, idx) => tokens[i + idx] && tokens[i + idx].word === w)
      );

      if (match) {
        phrasesMatchedInThisAnswer.add(match.phrase);
        i += match.n - 1; // skip the consumed words (loop's i++ covers the last one)
        continue;
      }

      if ((word.length < 3 && !isAcronym) || STOP_WORDS.has(word)) continue;

      const s = isAcronym ? word : stem(word);
      const key = `word:${s}`;
      if (!entries[key]) {
        entries[key] = { display: isAcronym ? raw : word, count: 0, isPhrase: false };
      }
      entries[key].count += 1;
      if (!isAcronym && word.length < entries[key].display.length) {
        entries[key].display = word;
      }
    }

    // Count each matched phrase once per answer (distinct-answer count),
    // matching the same metric used for the eligibility threshold above.
    for (const phrase of phrasesMatchedInThisAnswer) {
      const key = `phrase:${phrase}`;
      if (!entries[key]) {
        entries[key] = { display: phrase, count: 0, isPhrase: true };
      }
      entries[key].count += 1;
    }
  }

  return Object.values(entries);
}

// ── Seeded pseudo-random (stable per word) ────────────────────────────────────
// Gives the same color index every render for the same word/phrase, so
// entries don't jump around when the list updates.

function seededRandom(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return ((h >>> 0) / 0xffffffff);
}

// ── CSS keyframe injection (once) ─────────────────────────────────────────────

const STYLE_ID = 'quiz-wordcloud-keyframes';

function ensureKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes wc-pop {
      0%   { transform: translate(-50%, -50%) rotate(var(--wc-rot, 0deg)) scale(0.25); opacity: 0; }
      65%  { transform: translate(-50%, -50%) rotate(var(--wc-rot, 0deg)) scale(1.18); opacity: 1; }
      100% { transform: translate(-50%, -50%) rotate(var(--wc-rot, 0deg)) scale(1); opacity: 1; }
    }
    @keyframes wc-grow {
      0%   { transform: translate(-50%, -50%) rotate(var(--wc-rot, 0deg)) scale(1); }
      50%  { transform: translate(-50%, -50%) rotate(var(--wc-rot, 0deg)) scale(1.35); }
      100% { transform: translate(-50%, -50%) rotate(var(--wc-rot, 0deg)) scale(1); }
    }
    .wc-word {
      position: absolute;
      white-space: nowrap;
      transition: filter 0.15s;
    }
    .wc-word:hover {
      filter: brightness(1.25);
    }
    .wc-word--new  { animation: wc-pop  0.45s cubic-bezier(.34,1.56,.64,1) both; }
    .wc-word--grew { animation: wc-grow 0.35s cubic-bezier(.34,1.56,.64,1) both; }
  `;
  document.head.appendChild(style);
}

// ── Component ─────────────────────────────────────────────────────────────────

const FALLBACK_COLORS = ['#7B7C6A', '#FF6E83', '#575555', '#CC5869'];

// Minimum number of DISTINCT answers a 2-3 word phrase must appear in
// (verbatim) before it's grouped into a single cloud entry instead of being
// counted as separate single words. Exposed as a prop so it's easy to tune
// per-quiz later without touching the layout code.
const DEFAULT_PHRASE_MIN_ANSWER_COUNT = 2;

const LAYOUT_WIDTH = 900;
const LAYOUT_HEIGHT = 520;

export default function QuizWordCloud({ answerList, colorList, phraseMinAnswerCount = DEFAULT_PHRASE_MIN_ANSWER_COUNT }) {

  useEffect(() => { ensureKeyframes(); }, []);

  const colors = useMemo(() => {
    const list = (colorList || []).map(c => c.primaryCss).filter(Boolean);
    return list.length > 0 ? list : FALLBACK_COLORS;
  }, [colorList]);

  const words = useMemo(() => {
    const entryList = buildEntries(answerList, phraseMinAnswerCount);
    const entries = entryList
      .sort((a, b) => b.count - a.count)
      .slice(0, 80);

    if (entries.length === 0) return [];

    const maxCount = entries[0].count;
    const minCount = entries[entries.length - 1].count;
    const range = maxCount - minCount || 1;

    return entries.map(({ display, count, isPhrase }) => {
      const colorIndex = Math.floor(seededRandom(display + 'c') * colors.length);
      return {
        text: display,
        count,
        isPhrase,
        size: 14 + Math.round(((count - minCount) / range) * 42),
        weight: count >= maxCount * 0.6 ? 700 : count >= maxCount * 0.3 ? 500 : 400,
        wordColor: colors[colorIndex],
      };
    });
  }, [answerList, phraseMinAnswerCount, colors]);

  // ── Packed cloud layout (d3-cloud), recomputed whenever the word list changes ──
  const [positionedWords, setPositionedWords] = useState([]);

  useEffect(() => {
    if (words.length === 0) {
      setPositionedWords([]);
      return;
    }

    let cancelled = false;
    const layout = cloud()
      .size([LAYOUT_WIDTH, LAYOUT_HEIGHT])
      .words(words.map(w => ({ ...w })))
      .padding(4)
      .rotate(w => {
        const r = seededRandom(w.text + 'rot');
        if (r < 0.72) return 0;
        return r < 0.86 ? 30 : -30;
      })
      .font('Open Sans')
      .fontWeight(w => w.weight)
      .fontSize(w => w.size)
      .spiral('rectangular')
      .on('end', result => {
        if (!cancelled) setPositionedWords(result);
      });

    layout.start();
    return () => { cancelled = true; };
  }, [words]);

  // ── Responsive scaling: the layout itself is computed at a fixed pixel
  // size, then the whole thing is CSS-scaled down to fit narrower screens.
  // Uses a callback ref (not useRef + a []-effect) because the scaled div
  // only exists once there's at least one word - a plain effect would run
  // once against a still-null ref on the initial "Inga svar" render and
  // never fire again, leaving the cloud stuck unscaled. ──
  const observerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const setContainerRef = useCallback(node => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      // The scaled cloud's own height feeds back into this container's
      // (auto) height, so a naive "always setScale" here would re-trigger
      // the observer every time - only update when the value actually
      // changed, which lets the resulting no-op render break the loop.
      // The rAF deferral is the standard fix for the (otherwise harmless)
      // "ResizeObserver loop completed with undelivered notifications"
      // warning: it avoids re-measuring synchronously within the same
      // notification batch that triggered this callback.
      const updateScale = () => requestAnimationFrame(() => {
        setScale(prev => {
          const next = Math.min(1, node.clientWidth / LAYOUT_WIDTH);
          return Math.abs(next - prev) < 0.001 ? prev : next;
        });
      });
      updateScale();
      observerRef.current = new ResizeObserver(updateScale);
      observerRef.current.observe(node);
    }
  }, []);

  // Track which entries are new or grew since the last render
  const prevRef = useRef({});
  const animClass = useMemo(() => {
    const prev = prevRef.current;
    const result = {};
    for (const { text, count } of positionedWords) {
      if (!prev[text]) result[text] = 'wc-word--new';
      else if (count > prev[text]) result[text] = 'wc-word--grew';
    }
    return result;
  }, [positionedWords]);

  useEffect(() => {
    const next = {};
    for (const { text, count } of positionedWords) next[text] = count;
    prevRef.current = next;
  }, [positionedWords]);

  if (words.length === 0) {
    return (
      <div className="text-center text-muted mt-4" style={{ fontSize: '1.1rem' }}>
        Inga svar att visa ännu.
      </div>
    );
  }

  return (
    <div ref={setContainerRef} style={{ width: '100%', overflow: 'hidden' }}>
      <div style={{
        position: 'relative',
        width: LAYOUT_WIDTH * scale,
        height: LAYOUT_HEIGHT * scale,
        margin: '0 auto',
      }}>
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: LAYOUT_WIDTH,
          height: LAYOUT_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}>
          {positionedWords.map(({ text, x, y, rotate, size, weight, count, wordColor, isPhrase }) => (
            <span
              key={text}
              title={`${count} ${isPhrase ? 'svar med den här frasen' : (count === 1 ? 'svar' : 'svar')}`}
              className={`wc-word ${animClass[text] || ''}`}
              style={{
                left: LAYOUT_WIDTH / 2 + x,
                top: LAYOUT_HEIGHT / 2 + y,
                '--wc-rot': `${rotate || 0}deg`,
                transform: 'translate(-50%, -50%) rotate(var(--wc-rot, 0deg))',
                fontSize: `${size}px`,
                fontWeight: weight,
                color: wordColor,
                cursor: 'default',
              }}>
              {text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
